/**
 * useNotificationsHook — domain hook over the LOS `/notifications` endpoints +
 * the `notifications` Redux slice. Mirrors the other domain hooks (extend this,
 * don't call the api module directly from components).
 *
 * Responsibilities:
 *   - hydrate the inbox (list + unread count) on login / poll,
 *   - paginate older rows,
 *   - mark read / mark all read (optimistic Redux update, then server call),
 *   - load / save preferences,
 *   - surface a live notification as a toast (used by the RealtimeProvider).
 *
 * The realtime push itself is wired in realtime/RealtimeProvider.js; this hook is
 * the request/response + state half and works fully even when Web PubSub is dark.
 */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import CustomSnackbar from "../_src_shared/components/CustomSnackbar";
import { notificationsApi } from "../api/notificationsApi";
import {
  notificationsHydrate,
  notificationsAppend,
  notificationMarkRead,
  notificationsMarkAllRead,
  notificationsSetUnread,
  notificationsSetPrefs,
} from "../redux/reducers/notificationsReducer";

const PAGE_SIZE = 30;

// Map a notification severity → snackbar variant for the live toast.
const severityVariant = (severity) =>
  severity === "high" ? "error" : severity === "med" ? "warning" : "info";

export function useNotificationsHook() {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Pull the newest page + the unread badge count in one shot. Best-effort:
  // a failure (e.g. transient 5xx) leaves the existing state untouched.
  const hydrateInbox = useCallback(
    async ({ silent = true } = {}) => {
      try {
        const [items, count] = await Promise.all([
          notificationsApi.list({ limit: PAGE_SIZE }),
          notificationsApi.unreadCount(),
        ]);
        dispatch(
          notificationsHydrate({
            items: Array.isArray(items) ? items : [],
            unread: count?.unread ?? 0,
            hasMore: (items?.length || 0) >= PAGE_SIZE,
          }),
        );
        return items;
      } catch (err) {
        if (!silent) throw err;
        return null;
      }
    },
    [dispatch],
  );

  // Just the badge count (cheap poll while connected).
  const refreshUnread = useCallback(async () => {
    try {
      const count = await notificationsApi.unreadCount();
      dispatch(notificationsSetUnread(count?.unread ?? 0));
    } catch {
      // ignore — best effort
    }
  }, [dispatch]);

  // Older rows for the bell list. `beforeId` = the oldest _id currently held.
  const loadMore = useCallback(
    async (beforeId, filters = {}) => {
      try {
        const items = await notificationsApi.list({
          ...filters,
          limit: PAGE_SIZE,
          before_id: beforeId,
        });
        dispatch(
          notificationsAppend({
            items: Array.isArray(items) ? items : [],
            hasMore: (items?.length || 0) >= PAGE_SIZE,
          }),
        );
        return items;
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  const markRead = useCallback(
    async (id) => {
      if (!id) return;
      dispatch(notificationMarkRead(id)); // optimistic
      try {
        await notificationsApi.markRead(id);
      } catch {
        // The unread badge re-syncs on the next hydrate/poll, so a failed
        // mark-read is self-healing; no rollback needed for a UI hint.
      }
    },
    [dispatch],
  );

  const markAllRead = useCallback(async () => {
    dispatch(notificationsMarkAllRead()); // optimistic
    try {
      await notificationsApi.markAllRead();
    } catch {
      // self-healing on next hydrate
    }
  }, [dispatch]);

  const loadPreferences = useCallback(async () => {
    const data = await notificationsApi.getPreferences();
    dispatch(notificationsSetPrefs(data));
    return data;
  }, [dispatch]);

  const savePreferences = useCallback(
    async (body) => {
      const data = await notificationsApi.setPreferences(body);
      // PUT returns { preferences }; merge it back without losing the meta lists.
      dispatch(notificationsSetPrefs(data));
      enqueueSnackbar("Notification preferences saved", {
        autoHideDuration: 2400,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        content: (key, message) => (
          <CustomSnackbar
            id={key}
            message={message}
            variant="success"
            closeSnackbar={() => closeSnackbar(key)}
          />
        ),
      });
      return data;
    },
    [dispatch, enqueueSnackbar, closeSnackbar],
  );

  // Live toast for an arriving notification (med/high only — info/low just land
  // silently in the bell). Called by the RealtimeProvider on a push event.
  const toastNotification = useCallback(
    (n) => {
      if (!n) return;
      enqueueSnackbar(n.title || "New notification", {
        autoHideDuration: 5000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        content: (key, message) => (
          <CustomSnackbar
            id={key}
            message={message}
            variant={severityVariant(n.severity)}
            closeSnackbar={() => closeSnackbar(key)}
          />
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar],
  );

  return {
    hydrateInbox,
    refreshUnread,
    loadMore,
    markRead,
    markAllRead,
    loadPreferences,
    savePreferences,
    toastNotification,
  };
}

export default useNotificationsHook;
